import React from 'react';
import { Link } from 'react-router-dom';
import { UserAccountLayout } from '../components/UserAccountLayout';
import { useAppSelector, useAppDispatch } from '../../../shared/store/hooks';
import { removeFromWishlist } from '../../../shared/store/wishlistSlice';
import { addToCart } from '../../../shared/store/cartSlice';

interface Props { inAccountLayout?: boolean }

export const WishlistPage: React.FC<Props> = ({ inAccountLayout = false }) => {
    const dispatch = useAppDispatch();
    const items = useAppSelector(s => s.wishlist.items);

    const content = (
        <>
            <div className={inAccountLayout ? 'ua-page-header' : undefined} style={inAccountLayout ? undefined : { marginBottom: 24 }}>
                <h1 className={inAccountLayout ? 'ua-page-title' : undefined} style={inAccountLayout ? undefined : { fontSize: '1.6rem', fontWeight: 700, color: '#1a202c', margin: '0 0 4px' }}>
                    Wishlist
                </h1>
                <p className={inAccountLayout ? 'ua-page-subtitle' : undefined} style={inAccountLayout ? undefined : { color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                    {items.length > 0
                        ? `${items.length} saved item${items.length > 1 ? 's' : ''}`
                        : 'Save items to revisit them later.'}
                </p>
            </div>

            {items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 12 }}>🤍</div>
                    <p style={{ fontSize: '1rem', color: '#475569', margin: '0 0 6px', fontWeight: 600 }}>Your wishlist is empty</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0 0 24px' }}>Browse products and tap the heart icon to save items here.</p>
                    <Link
                        to="/products"
                        style={{ display: 'inline-block', padding: '10px 28px', background: '#1565c0', color: '#fff', borderRadius: 50, fontWeight: 700, textDecoration: 'none', fontSize: '0.875rem' }}
                    >
                        Browse Products
                    </Link>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                    {items.map(item => (
                        <WishlistCard
                            key={item.productId}
                            item={item}
                            onRemove={() => dispatch(removeFromWishlist(item.productId))}
                            onAddToCart={() => dispatch(addToCart({ productId: item.productId, name: item.name, brand: item.brand, price: item.price, image: item.image, category: item.category }))}
                        />
                    ))}
                </div>
            )}
        </>
    );

    return (
        <>
            {inAccountLayout ? (
                <UserAccountLayout>{content}</UserAccountLayout>
            ) : (
                <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '48px 0 80px', fontFamily: "'Open Sans', sans-serif" }}>
                    <div className="container">
                        <nav style={{ marginBottom: 24, fontSize: '0.85rem', color: '#6b7280' }}>
                            <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                            <span style={{ margin: '0 8px' }}>›</span>
                            <span>Wishlist</span>
                        </nav>
                        {content}
                    </div>
                </div>
            )}
        </>
    );
};

interface CardProps {
    item: { productId: string; name: string; brand: string; price: number; image: string; category: string };
    onRemove: () => void;
    onAddToCart: () => void;
}

const WishlistCard: React.FC<CardProps> = ({ item, onRemove, onAddToCart }) => (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
        {/* Image */}
        <div style={{ position: 'relative', height: 180, background: '#f8fafc', overflow: 'hidden' }}>
            {item.image ? (
                <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#cbd5e1' }}>🖼️</div>
            )}
            {/* Remove heart */}
            <button
                onClick={onRemove}
                title="Remove from wishlist"
                style={{ position: 'absolute', top: 10, right: 10, width: 32, height: 32, borderRadius: '50%', background: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', color: '#ef4444' }}
            >
                ♥
            </button>
        </div>

        {/* Info */}
        <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{item.brand}</p>
            <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#1a202c', lineHeight: 1.3 }}>{item.name}</p>
            <p style={{ margin: '4px 0 0', fontSize: '1rem', fontWeight: 700, color: '#1565c0' }}>₹{item.price.toLocaleString('en-IN')}</p>
        </div>

        {/* Actions */}
        <div style={{ padding: '0 16px 16px', display: 'flex', gap: 8 }}>
            <button
                onClick={onAddToCart}
                style={{ flex: 1, padding: '8px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
            >
                Add to Cart
            </button>
            <Link
                to={`/products/${item.productId}`}
                style={{ padding: '8px 14px', border: '1.5px solid #d1d5db', color: '#374151', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
            >
                View
            </Link>
        </div>
    </div>
);
